import { Controller } from '@hotwired/stimulus'

// Maps each Yes/No question to the follow-up field a "No" answer requires,
// mirroring TimeStudy::Response#derived_review_status.
const FOLLOW_UPS = {
  'time_study_response[activity_verifiable]': 'time_study_response[verification_failure_reason]',
  'time_study_response[code_correct]': 'time_study_response[corrective_action]'
}

// Auto-saves supervisor review rows. Lives on the table wrapper; the row inputs
// sit in grid cells and associate with their row's form via the HTML form
// attribute, so any change (Yes/No radios on click, textareas on blur) resubmits
// that form, which refreshes the page with a morph.
//
// Because that refresh re-renders the whole page, the morph guards below keep it
// from destroying work that hasn't reached the server: fields the reviewer is
// still editing are skipped, and rows that fell out of the active scope chip
// (e.g. completed under "Pending") stay in place until a real navigation.
export default class extends Controller {
  connect() {
    this.keptRows = new Set()
    this.restoreRadioState()
    this.morphListener = () => this.afterMorph()
    this.beforeRenderListener = () => this.snapshotRowOrder()
    this.beforeMorphElementListener = event => this.beforeMorphElement(event)
    document.addEventListener('turbo:morph', this.morphListener)
    document.addEventListener('turbo:before-render', this.beforeRenderListener)
    document.addEventListener('turbo:before-morph-element', this.beforeMorphElementListener)
  }

  disconnect() {
    document.removeEventListener('turbo:morph', this.morphListener)
    document.removeEventListener('turbo:before-render', this.beforeRenderListener)
    document.removeEventListener('turbo:before-morph-element', this.beforeMorphElementListener)
  }

  save(event) {
    const input = event.target
    if (input.type === 'radio') {
      this.syncCheckedData(input)
      this.toggleFollowUp(input)
    } else if (input.matches('.ts-review-table__textarea--follow-up')) {
      input.classList.toggle('ts-review-table__textarea--required', !input.value.trim())
    }
    if (input.form) input.form.requestSubmit()
  }

  // The server renders both follow-up textareas and hides the one whose question
  // isn't answered "No" (clearing its stored text on save) — but a kept row never
  // sees that render, so mirror it here: hide and clear on Yes, reveal empty and
  // required on No. Runs before the submit so the cleared value is what's saved.
  toggleFollowUp(radio) {
    const note = radio.closest('.table__row')?.querySelector(`textarea[name="${FOLLOW_UPS[radio.name]}"]`)
    if (!note) return

    const answeredNo = radio.value === 'false'
    note.classList.toggle('d-none', !answeredNo)
    if (!answeredNo) note.value = ''
    note.classList.toggle('ts-review-table__textarea--required', !note.value.trim())
  }

  beforeMorphElement(event) {
    const el = event.target
    if (!this.element.contains(el)) return

    if (event.detail.newElement === undefined) {
      this.guardRemoval(el, event)
    } else if (this.isTextarea(el) && el.value !== event.detail.newElement.value && this.editedByUser(el)) {
      event.preventDefault()
    }
  }

  afterMorph() {
    this.restoreRadioState()
    this.restoreRowOrder()
    this.keptRows.forEach(row => row.isConnected && this.updateRowCheck(row))
    this.keptRows.clear()
    this.rowOrderBeforeMorph = null
  }

  snapshotRowOrder() {
    this.rowOrderBeforeMorph = this.rows().map(row => row.id)
  }

  rows() {
    return Array.from(this.element.querySelectorAll('.table__row[id]'))
  }

  // Idiomorph won't scan past the element holding focus when matching rows, so
  // rows morphing in below a kept row that holds focus (the one the reviewer is
  // working in) get moved above it instead, sinking the kept row to the bottom
  // of the table. Put every row back into its pre-morph order; rows the render
  // pulled in fresh sort after all the rows the reviewer could already see.
  restoreRowOrder() {
    const previous = this.rowOrderBeforeMorph
    if (!previous?.length) return

    const indexes = new Map(previous.map((id, index) => [id, index]))
    const rank = row => (indexes.has(row.id) ? indexes.get(row.id) : Infinity)
    const target = this.rows().sort((a, b) => rank(a) - rank(b))
    target.forEach((row, index) => {
      if (index === 0 || row.previousElementSibling === target[index - 1]) return

      this.moveRow(row, target[index - 1].nextSibling)
    })
  }

  // moveBefore relocates the row without resetting its state — crucially focus,
  // which the reviewer may hold in the row being moved. Where it isn't
  // implemented, fall back to insertBefore and restore focus/selection by hand.
  moveRow(row, anchor) {
    const parent = row.parentElement
    if (typeof parent.moveBefore === 'function') {
      parent.moveBefore(row, anchor)
      return
    }

    const active = document.activeElement
    const focusWithin = active && row.contains(active)
    const selection =
      focusWithin && active.tagName === 'TEXTAREA'
        ? [active.selectionStart, active.selectionEnd, active.selectionDirection]
        : null
    parent.insertBefore(row, anchor)
    if (focusWithin && document.activeElement !== active) {
      active.focus({ preventScroll: true })
      if (selection) active.setSelectionRange(...selection)
    }
  }

  // While Turbo swaps or morphs the body, the radios' form-attribute association
  // is unresolved, so all rows' same-named radios act as one group and every
  // checked radio but the document's last gets unchecked. Morphs corrupt even the
  // checked attribute (idiomorph syncs it from the collapsed tree's properties),
  // so re-assert state from data-checked, which morphs copy verbatim.
  restoreRadioState() {
    this.element.querySelectorAll('.ts-yes-no__input').forEach(input => {
      const checked = input.dataset.checked === 'true'
      input.checked = checked
      input.defaultChecked = checked
    })
  }

  // A morphed-away element is one the refreshed render no longer includes. Rows
  // (and their hidden forms, without which a row's radios lose their grouping and
  // it can't save) are kept so completing a review doesn't jump the page; a
  // follow-up textarea is kept only while it holds unsaved text.
  guardRemoval(el, event) {
    if (el.matches('.table__row')) {
      event.preventDefault()
      this.keptRows.add(el)
    } else if (el.tagName === 'FORM' && el.id.startsWith('ts-review-form-')) {
      event.preventDefault()
    } else if (this.isTextarea(el) && this.editedByUser(el)) {
      event.preventDefault()
    }
  }

  isTextarea(el) {
    return el.matches?.('.ts-review-table__textarea')
  }

  // Focused, or holding text the server hasn't confirmed back yet.
  editedByUser(el) {
    return el === document.activeElement || el.value !== el.defaultValue
  }

  // Mirrors a radio click into its group's data-checked so restoreRadioState
  // (re-asserting after each morph) can't undo a selection the server render
  // doesn't know about yet — kept rows never receive a fresh render at all.
  syncCheckedData(radio) {
    const group = `.ts-yes-no__input[name="${radio.name}"][form="${radio.getAttribute('form')}"]`
    this.element.querySelectorAll(group).forEach(input => {
      input.dataset.checked = input.checked.toString()
    })
  }

  // A kept row isn't in the refreshed render, so derive its status icon from its
  // own inputs (the client-side mirror of TimeStudy::Response#derived_review_status).
  updateRowCheck(row) {
    const check = row.querySelector('.ts-review-table__check')
    if (!check) return

    check.classList.toggle('ts-review-table__check--pending', !this.rowReviewComplete(row))
  }

  rowReviewComplete(row) {
    return Object.entries(FOLLOW_UPS).every(([question, followUp]) => {
      const answer = row.querySelector(`.ts-yes-no__input[name="${question}"]:checked`)
      if (!answer) return false
      if (answer.value === 'true') return true

      const note = row.querySelector(`textarea[name="${followUp}"]`)
      return Boolean(note && note.value.trim())
    })
  }
}
